import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCalendar } from 'react-icons/fi';

interface AddTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  txType: 'Expense' | 'Revenue';
  txTitle: string;
  setTxTitle: (val: string) => void;
  txAmount: string;
  setTxAmount: (val: string) => void;
  txDate: string;
  setTxDate: (val: string) => void;
  txCategory: string;
  setTxCategory: (val: string) => void;
  txNotes: string;
  setTxNotes: (val: string) => void;
  projectCategories: any[];
}

export const AddTransactionModal: React.FC<AddTransactionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  txType,
  txTitle,
  setTxTitle,
  txAmount,
  setTxAmount,
  txDate,
  setTxDate,
  txCategory,
  setTxCategory,
  txNotes,
  setTxNotes,
  projectCategories,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-[#0B0B0B] border border-white/10 rounded-[28px] p-6 text-left flex flex-col max-h-[85vh] z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-white">Add {txType}</h2>
                <p className="text-zinc-550 text-xs mt-1">
                  {txType === 'Expense'
                    ? 'Record an expense against this project.'
                    : 'Record income earned against this project.'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-zinc-400 text-xs font-semibold mb-2">Source</label>
                <input
                  type="text"
                  placeholder={
                    txType === 'Expense' ? 'e.g. DJ Booth Rental' : 'e.g. Ticket Sales — Night 1'
                  }
                  value={txTitle}
                  onChange={(e) => setTxTitle(e.target.value)}
                  required
                  className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-2">Amount</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={txAmount}
                    onChange={(e) => setTxAmount(e.target.value)}
                    required
                    className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-2">Date</label>
                  <div className="relative">
                    <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-550 w-4 h-4" />
                    <input
                      type="date"
                      value={txDate}
                      onChange={(e) => setTxDate(e.target.value)}
                      required
                      className="w-full bg-[#0F0F0F] border border-white/5 outline-none text-white text-xs rounded-xl pl-11 pr-4 py-3.5 focus:border-blue-500 transition-all [color-scheme:dark]"
                    />
                  </div>
                </div>
              </div>

              {txType === 'Expense' ? (
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-2">
                    Category Assignment
                  </label>
                  <div className="flex flex-wrap gap-2.5">
                    {projectCategories.map((c) => {
                      const isSelected = txCategory === c.name;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setTxCategory(c.name)}
                          className={`px-4 py-2 rounded-full border text-xs font-semibold cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-blue-500/10 border-blue-500/30 text-blue-450'
                              : 'border-white/5 bg-[#0F0F0F]/60 text-zinc-400 hover:text-white hover:border-white/10'
                          }`}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-2">
                    External Link <span className="text-zinc-550">(Category Select)</span>
                  </label>
                  <div className="flex flex-wrap gap-2.5">
                    {[
                      { name: 'Ticket Sales', color: 'emerald' },
                      { name: 'VIP', color: 'amber' },
                      { name: 'Sponsorship', color: 'blue' },
                      { name: 'Bar Revenue', color: 'purple' },
                      { name: 'Merchandise', color: 'teal' },
                      { name: 'Other', color: 'zinc' },
                    ].map((cat) => {
                      const isSelected = txCategory === cat.name;
                      let activeStyles = '';
                      if (isSelected) {
                        if (cat.color === 'emerald')
                          activeStyles = 'bg-emerald-500/10 border-emerald-500/30 text-emerald-450';
                        else if (cat.color === 'amber')
                          activeStyles = 'bg-amber-500/10 border-amber-500/30 text-amber-450';
                        else if (cat.color === 'blue')
                          activeStyles = 'bg-blue-500/10 border-blue-500/30 text-blue-450';
                        else if (cat.color === 'purple')
                          activeStyles = 'bg-purple-500/10 border-purple-500/30 text-purple-450';
                        else if (cat.color === 'teal')
                          activeStyles = 'bg-teal-500/10 border-teal-500/30 text-teal-450';
                        else activeStyles = 'bg-zinc-800 border-zinc-700 text-white';
                      }
                      return (
                        <button
                          key={cat.name}
                          type="button"
                          onClick={() => setTxCategory(cat.name)}
                          className={`px-4 py-2 rounded-full border text-xs font-semibold cursor-pointer transition-all ${
                            isSelected
                              ? activeStyles
                              : 'border-white/5 bg-[#0F0F0F]/60 text-zinc-400 hover:text-white hover:border-white/10'
                          }`}
                        >
                          {cat.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-zinc-400 text-xs font-semibold mb-2">
                  Notes (Internal) <span className="text-zinc-550">(Optional)</span>
                </label>
                <textarea
                  placeholder="Any notes about this transaction..."
                  value={txNotes}
                  onChange={(e) => setTxNotes(e.target.value)}
                  rows={4}
                  className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3.5 resize-none transition-all"
                />
              </div>

              <div className="flex items-center justify-end pt-4 border-t border-white/5">
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-white text-black hover:bg-zinc-100 rounded-full text-xs font-bold cursor-pointer transition-all active:scale-95 shadow-lg shadow-white/5"
                >
                  Add {txType}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
